import { TrendingUp, Activity, DollarSign, Globe, Target, CreditCard } from 'lucide-react';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';
import { formatCurrencyValue, formatPercent, formatFiscalYear } from '../utils/formatters';
// The KPI tile lives in ui/ now — this row was its only home, and the Data
// Quality console needs the same signature so both read as one app.
import MetricCard from '../../ui/StatTile';

interface Props {
  latest: UnifiedFinancialRecord;
  region?: RegionConfig;
}

export default function MetricCardsRow({ latest, region }: Props) {
  const year = formatFiscalYear(latest.year, region);
  const unit = region?.currency?.unit_abbr ?? '';

  const revenue = latest.total_revenue ?? latest.revenue_from_operations;

  const patMargin =
    latest.net_profit != null && revenue != null && revenue > 0
      ? latest.net_profit / revenue
      : null;

  const ebitMargin =
    latest.ebit != null && revenue != null && revenue > 0
      ? latest.ebit / revenue
      : null;

  const netDebt =
    latest.net_debt ??
    ((latest.total_debt ?? 0) - (latest.cash_and_equivalents ?? 0) || null);

  const card3 =
    latest.net_profit != null
      ? { label: 'Net Profit', value: formatCurrencyValue(latest.net_profit, region), sub: patMargin != null ? `PAT Margin: ${formatPercent(patMargin)}` : year, iconBg: 'bg-teal-50', iconColor: 'text-teal-600', icon: <DollarSign className="h-4 w-4" /> }
      : latest.ebit != null
        ? { label: 'EBIT', value: formatCurrencyValue(latest.ebit, region), sub: ebitMargin != null ? `EBIT Margin: ${formatPercent(ebitMargin)}` : year, iconBg: 'bg-teal-50', iconColor: 'text-teal-600', icon: <DollarSign className="h-4 w-4" /> }
        : { label: 'Invested Capital', value: formatCurrencyValue(latest.invested_capital, region), sub: year, iconBg: 'bg-teal-50', iconColor: 'text-teal-600', icon: <DollarSign className="h-4 w-4" /> };

  const roicAboveWacc = latest.roic != null && latest.wacc != null && latest.roic > latest.wacc;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      <MetricCard
        label="Revenue"
        value={formatCurrencyValue(revenue, region)}
        sub={`${year} · ${unit}`}
        iconBg="bg-blue-50"
        iconColor="text-blue-600"
        icon={<TrendingUp className="h-4 w-4" />}
      />
      <MetricCard
        label="EBITDA"
        value={formatCurrencyValue(latest.ebitda, region)}
        sub={latest.ebitda_margin != null ? `Margin: ${formatPercent(latest.ebitda_margin)}` : year}
        iconBg="bg-indigo-50"
        iconColor="text-indigo-600"
        icon={<Activity className="h-4 w-4" />}
      />
      <MetricCard
        label={card3.label}
        value={card3.value}
        sub={card3.sub}
        iconBg={card3.iconBg}
        iconColor={card3.iconColor}
        icon={card3.icon}
      />
      <MetricCard
        label="EV"
        value={formatCurrencyValue(latest.enterprise_value, region)}
        sub={latest.ev_ebitda != null ? `EV/EBITDA: ${latest.ev_ebitda.toFixed(1)}x` : year}
        iconBg="bg-violet-50"
        iconColor="text-violet-600"
        icon={<Globe className="h-4 w-4" />}
      />
      <MetricCard
        label="ROIC"
        value={formatPercent(latest.roic)}
        sub={latest.wacc != null ? `WACC: ${formatPercent(latest.wacc)}` : year}
        iconBg={roicAboveWacc ? 'bg-green-50' : 'bg-red-50'}
        iconColor={roicAboveWacc ? 'text-green-600' : 'text-red-500'}
        icon={<Target className="h-4 w-4" />}
      />
      <MetricCard
        label="Net Debt"
        value={formatCurrencyValue(netDebt, region)}
        sub={unit || year}
        iconBg="bg-neutral-100"
        iconColor="text-neutral-500"
        icon={<CreditCard className="h-4 w-4" />}
      />
    </div>
  );
}
